import { PrimaryButton } from "@fluentui/react";
import { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { resetQualityHubPinAsync } from "src/api";
import { localize } from "src/l10n";
import { UnstyledButton } from "src/ui";
import SpintrUser from "src/ui/components/SpintrUser";
import { Portal } from "../Portal";
import { CaptionBody, ErrorText, ExtraLargeHeading, LargeBody } from "../Text";
import { QualityHubPinResetModalProps } from "./QualityHubPinResetModal.types";

const pinLength = 4;
const pinIndices = Array.from({ length: pinLength }, (_, index) => index);

function QualityHubPinResetModal({
    user,
    onDismiss,
}: QualityHubPinResetModalProps): JSX.Element {
    const isSmallViewMode = useSelector<Spintr.AppState, boolean>(
        (state) => state.ui.isSmallViewMode
    );
    const pinInputRef = useRef<HTMLInputElement>(null);
    const [pinCode, setPinCode] = useState("");
    const [error, setError] = useState<string | undefined>();
    const [isSubmitting, setIsSubmitting] = useState(false);

    const canSubmit = useMemo(() => pinCode.length === pinLength && !isSubmitting, [pinCode.length, isSubmitting]);

    useEffect(() => {
        pinInputRef.current?.focus();
    }, []);

    const onPinInputChanged = useCallback((event: ChangeEvent<HTMLInputElement>) => {
        const digitsOnly = event.target.value.replace(/\D/g, "").slice(0, pinLength);

        setPinCode(digitsOnly);
        setError(undefined);
    }, []);

    const onResetPinClicked = useCallback(async () => {
        if (pinCode.length !== pinLength) {
            setError(localize("QH_RESET_PIN_LENGTH_ERROR"));
            return;
        }

        setIsSubmitting(true);
        setError(undefined);

        try {
            await resetQualityHubPinAsync(user.id, pinCode);
            onDismiss();
        } catch {
            setError(localize("QH_RESET_PIN_FAILED"));
            setIsSubmitting(false);
        }
    }, [onDismiss, pinCode, user.id]);

    const onCloseClicked = useCallback(() => {
        onDismiss();
    }, [onDismiss]);

    return (
        <Portal>
            <div className={`QualityHubPinResetModal ${isSmallViewMode ? "small" : "desktop"}`}>
                <div className="QualityHubPinResetModal-overlay" />
                <div className="QualityHubPinResetModal-shell">
                    <div className="QualityHubPinResetModal-content">
                        <div className="QualityHubPinResetModal-body">
                            <LargeBody
                                className="QualityHubPinResetModal-heading"
                                color="contentDark"
                                weight="semiBold">
                                {localize("QH_RESET_PIN")}
                            </LargeBody>
                            <CaptionBody className="QualityHubPinResetModal-subHeading">
                                {localize("QH_RESET_PIN_INSTRUCTIONS")}
                            </CaptionBody>
                            <div className="QualityHubPinResetModal-user">
                                <SpintrUser name={user.name}
                                    personalName={true}
                                    subText={user.title.join(", ")}
                                    imageUrl={user.image} />
                            </div>
                            <div className="QualityHubPinResetModal-pinBoxes">
                                <input
                                    autoComplete="off"
                                    className="QualityHubPinResetModal-hiddenInput"
                                    inputMode="numeric"
                                    maxLength={pinLength}
                                    onChange={onPinInputChanged}
                                    ref={pinInputRef}
                                    type="tel"
                                    value={pinCode}
                                />
                                {pinIndices.map((index) => (
                                    <ExtraLargeHeading
                                        className="QualityHubPinResetModal-pinBox"
                                        color="black"
                                        key={index}
                                        onClick={() => pinInputRef.current?.focus?.()}
                                        onKeyDown={(ev) => {
                                            if (ev.key === "Enter" || ev.key === " ") {
                                                ev.preventDefault();
                                                pinInputRef.current?.focus?.();
                                            }
                                        }}
                                        role="button"
                                        tabIndex={-1}>
                                        {pinCode[index] ?? "_"}
                                    </ExtraLargeHeading>
                                ))}
                            </div>

                            {error && (
                                <ErrorText className="QualityHubPinResetModal-errorText">
                                    {error}
                                </ErrorText>
                            )}
                        </div>
                    </div>
                    <div className="QualityHubPinResetModal-actions">
                        <PrimaryButton
                            className="QualityHubPinResetModal-submitButton"
                            disabled={!canSubmit}
                            onClick={onResetPinClicked}
                            text={localize("QH_RESET_PIN_SAVE")}
                        />
                        <UnstyledButton
                            className="QualityHubPinResetModal-cancelButton"
                            onClick={onCloseClicked}>
                            {localize("QH_CANCEL")}
                        </UnstyledButton>
                    </div>
                </div>
            </div>
        </Portal>
    );
}

export default QualityHubPinResetModal;
